import React from "react";
import Logo, { LOGO_WIDE_URL } from "./Logo";
import SafeImage from "./SafeImage";
import AuthorCard from "./AuthorCard";

// Typography + palette of the final PDF layout (Times / Helvetica, red, black, cream).
export const TIMES = "'Times New Roman', Tinos, Times, serif";
export const HELV = "'Helvetica Neue', Helvetica, Arimo, Arial, sans-serif";
export const RED = "#E31E24";
export const INK = "#0D0D0D";
export const CREAM = "#E5DBAB";

export const pick = (bi, lang) => (bi && typeof bi === "object" ? bi[lang] || bi.fr || bi.en || "" : bi || "");

export const pillarsOf = (data, lang) => [1, 2, 3].map((i) => ({
  n: i,
  title: pick(data[`pillar${i}_title`], lang),
  body: pick(data[`pillar${i}_body`], lang),
}));

export function Sheet({ children, testid, wide = true }) {
  return (
    <div
      className={`mx-auto bg-white ${wide ? "max-w-[880px]" : "max-w-[720px]"}`}
      style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.06), 0 18px 50px -20px rgba(0,0,0,0.25)" }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

// Official logo image, cropped to its visible artwork (the source file has wide white margins).
export function LogoCrop({ h, className = "" }) {
  const imgH = h * 1.7;
  return (
    <div className={`overflow-hidden shrink-0 ${className}`} style={{ height: h, width: imgH * 2 * 0.9 }}>
      <img src={LOGO_WIDE_URL} crossOrigin="anonymous" alt="GLOBAL AFRIQUE INTERNATIONAL — Communication & Régie" translate="no"
        className="notranslate block max-w-none" draggable="false" data-testid="gai-logo-sheet"
        style={{ height: imgH, width: imgH * 2, marginTop: -imgH * 0.1375, marginLeft: -imgH * 0.1125 }} />
    </div>
  );
}

function SheetHeader() {
  return (
    <div>
      <div className="flex items-end gap-4 md:gap-6">
        <div className="flex items-end gap-[4px] shrink-0" aria-hidden>
          <span className="block w-[9px] md:w-[11px] h-[52px] md:h-[72px]" style={{ background: RED }} />
          <span className="block w-[9px] md:w-[11px] h-[38px] md:h-[54px]" style={{ background: INK }} />
        </div>
        <LogoCrop h={38} className="md:hidden mb-1" />
        <LogoCrop h={56} className="hidden md:block mb-1.5" />
      </div>
      <div className="h-[7px] md:h-[9px] ml-[22px] md:ml-[26px]" style={{ background: CREAM }} aria-hidden />
    </div>
  );
}

const Kicker = ({ children, className = "", color = RED, testid }) => (
  <div className={`uppercase font-bold text-[13px] md:text-[15px] tracking-[0.01em] ${className}`} style={{ fontFamily: HELV, color }} data-testid={testid}>
    {children}
  </div>
);

const Body = ({ children, className = "" }) => (
  <p className={`text-[16px] md:text-[17px] leading-[1.5] ${className}`} style={{ fontFamily: TIMES, color: "#111" }}>{children}</p>
);

function SectionBlock({ section, lang, i }) {
  return (
    <div data-testid={`special-section-${i}`}>
      <Kicker className="!text-[13px] md:!text-[14px]">{pick(section.title, lang)}</Kicker>
      <div className="w-[38px] h-[2px] mt-1.5 mb-3" style={{ background: RED }} aria-hidden />
      <Body>{pick(section.body, lang)}</Body>
    </div>
  );
}

export function CoverSheet({ data, lang }) {
  if (data.cover_design_url) {
    return (
      <Sheet wide={false} testid="special-cover-sheet">
        <SafeImage
          src={data.cover_design_url}
          alt={`${pick(data.name_title, lang)} ${data.name_line1} ${data.name_line2} ${data.name_accent}`}
          className="block w-full h-auto"
          style={{ width: "100%", height: "auto", aspectRatio: "2 / 3" }}
          loading="eager"
          testid="special-cover-design"
        />
      </Sheet>
    );
  }
  return (
    <Sheet wide={false} testid="special-cover-sheet">
      <CoverReplica data={data} lang={lang} />
    </Sheet>
  );
}

// HTML recreation of the PDF cover — used when no designed cover image is uploaded.
function CoverReplica({ data, lang }) {
  const pillars = pillarsOf(data, lang);
  const cq = (v) => `${v}cqw`;
  return (
    <div className="relative overflow-hidden bg-white" style={{ aspectRatio: "2 / 3", containerType: "inline-size", fontFamily: TIMES }} data-testid="special-cover-replica">
      <SafeImage src={data.cover_photo_url} alt="" className="absolute object-cover"
        style={{ top: "15%", left: "22%", right: 0, bottom: 0, width: "78%", height: "85%", objectPosition: data.photo_focus || "center 20%" }} />
      <div className="absolute" style={{ top: "15%", left: "10%", right: 0, bottom: 0, background: "linear-gradient(90deg,#fff 0%,#fff 22%,rgba(255,255,255,0.75) 42%,rgba(255,255,255,0) 62%)" }} />
      <div className="absolute top-0 bottom-0 left-0" style={{ width: "7.5%", background: RED }} />
      <div className="absolute bottom-0" style={{ top: "3.5%", left: "7.5%", width: "3.5%", background: INK }} />
      <div className="absolute flex justify-center" style={{ top: "3.5%", left: "14%", right: "4%" }}><Logo size={9999} style={{ height: "auto", width: "80%" }} /></div>
      <div className="absolute" style={{ top: "12.3%", left: "11%", right: "4%", height: "1.6%", background: CREAM }} />
      <div className="absolute" style={{ top: "16.5%", left: "15.5%", width: "55%", color: INK }}>
        <div className="uppercase" style={{ fontSize: cq(1.9), letterSpacing: "0.35em", fontWeight: 700 }}>{pick(data.kicker, lang)}</div>
        <div style={{ width: "22%", height: cq(0.3), background: RED, marginTop: cq(1.2) }} />
        <div className="uppercase font-bold" style={{ color: RED, fontSize: cq(2.5), marginTop: cq(2.2), lineHeight: 1.15 }}>{pick(data.name_title, lang)}</div>
        <div className="uppercase font-bold" style={{ fontSize: cq(10.5), lineHeight: 0.92, marginTop: cq(1), letterSpacing: "-0.02em", transform: "scaleX(0.86)", transformOrigin: "left" }} translate="no">
          {data.name_line1}<br />{data.name_line2}<br />{data.name_accent}
        </div>
        <div className="inline-block uppercase font-bold text-white" style={{ background: RED, fontSize: cq(4.2), lineHeight: 1.12, padding: `${cq(0.8)} ${cq(1.8)}`, marginTop: cq(1.2) }}>{pick(data.subtitle_italic, lang)}</div>
        <div className="uppercase font-bold" style={{ fontSize: cq(3.1), lineHeight: 1.15, marginTop: cq(3) }}>{pick(data.event_full_title, lang)}</div>
        <div className="uppercase font-bold" style={{ fontSize: cq(2.1), lineHeight: 1.4, marginTop: cq(0.8) }}>{pick(data.event_date_full, lang)}<br />{data.event_place}</div>
        <div style={{ borderLeft: `${cq(0.6)} solid ${RED}`, paddingLeft: cq(2.4), marginTop: cq(4), fontSize: cq(2.6), lineHeight: 1.35, fontWeight: 700 }}>
          {pillars.map((p) => <div key={p.n}>{p.title}</div>)}
        </div>
      </div>
      <div className="absolute bg-white" style={{ left: "11%", bottom: 0, width: "40%", padding: `${cq(3)} ${cq(4.5)}`, color: INK }}>
        <div className="font-bold uppercase" style={{ fontSize: cq(2.8), lineHeight: 1.15 }} translate="no">GLOBAL AFRIQUE INTERNATIONAL</div>
        <div className="italic" style={{ fontSize: cq(2.2), marginTop: cq(0.6) }}>{lang === "fr" ? "L'Afrique au cœur du monde." : "Africa at the heart of the world."}</div>
        <div style={{ width: "30%", height: cq(0.3), background: RED, marginTop: cq(1.6) }} />
      </div>
    </div>
  );
}

export function ArticleSheet({ data, lang }) {
  const sections = data.article_sections || [];
  const photo = data.article_photo_url || data.cover_photo_url;
  const closing = pick(data.closing_note, lang);
  return (
    <Sheet testid="special-article-sheet">
      <div className="px-5 sm:px-10 md:px-14 pt-8 md:pt-11 pb-10 md:pb-14">
        <SheetHeader />
        <Kicker className="mt-9 md:mt-11" testid="special-article-kicker">{pick(data.article_kicker, lang)}</Kicker>
        <h2 className="mt-3 md:mt-4 font-bold text-[30px] sm:text-[38px] md:text-[44px] leading-[1.12] max-w-[640px]"
          style={{ fontFamily: TIMES, color: INK, letterSpacing: 0 }} data-testid="special-article-title">
          {pick(data.article_title, lang)}
        </h2>

        <div className="mt-9 md:mt-11 grid md:grid-cols-[1fr_31%] gap-8 md:gap-12 items-start">
          <div className="space-y-7">
            {sections.slice(0, 2).map((s, i) => <SectionBlock key={i} section={s} lang={lang} i={i} />)}
          </div>
          {photo && (
            <SafeImage src={photo} alt={`${data.name_line1} ${data.name_line2} ${data.name_accent}`} className="w-full object-cover md:mt-1"
              style={{ width: "100%", aspectRatio: "223 / 257", objectPosition: data.photo_focus || "center 20%" }} testid="special-article-photo" />
          )}
        </div>

        {sections.length > 2 && (
          <div className="mt-12 md:mt-20 space-y-7">
            {sections.slice(2).map((s, i) => <SectionBlock key={i + 2} section={s} lang={lang} i={i + 2} />)}
          </div>
        )}

        {data.author && <AuthorCard author={data.author} />}

        {closing && (
          <div className="mt-12 md:mt-16 px-5 md:px-6 py-5 md:py-6" style={{ background: CREAM }} data-testid="special-closing-note">
            <p className="italic text-[17px] md:text-[19px] leading-snug" style={{ fontFamily: TIMES, color: INK }}>{closing}</p>
          </div>
        )}
      </div>
    </Sheet>
  );
}

export function ForumSheet({ data, lang }) {
  const pillars = pillarsOf(data, lang);
  return (
    <Sheet testid="special-forum-sheet">
      <div className="px-5 sm:px-10 md:px-14 pt-8 md:pt-11 pb-12 md:pb-16">
        <SheetHeader />
        <Kicker className="mt-9 md:mt-11">{pick(data.event_full_title, lang)}</Kicker>
        <h2 className="mt-3 font-bold text-[38px] sm:text-[48px] md:text-[56px] leading-[1.05]" style={{ fontFamily: TIMES, color: INK, letterSpacing: 0 }} translate="no" data-testid="special-forum-title">
          {data.event_short_title}
        </h2>
        <div className="mt-2 font-bold text-[21px] sm:text-[26px] md:text-[30px] leading-tight" style={{ fontFamily: TIMES, color: INK }}>
          {pick(data.event_subtitle_italic, lang)}
        </div>
        <Kicker className="mt-4" color={INK}>{pick(data.event_dateline, lang)}</Kicker>

        <div className="mt-12 md:mt-16 space-y-8 md:space-y-10" data-testid="special-forum-axes">
          {pillars.map((p) => (
            <div key={p.n} className="flex items-start gap-5 md:gap-7">
              <div className="shrink-0 w-[42px] h-[42px] md:w-[46px] md:h-[46px] flex items-center justify-center text-white font-bold text-[22px]" style={{ background: RED, fontFamily: TIMES }}>
                {p.n}
              </div>
              <div className="pt-0.5">
                <div className="uppercase font-bold text-[20px] md:text-[23px] leading-tight" style={{ fontFamily: TIMES, color: RED }}>{p.title}</div>
                <Body className="mt-2 max-w-[640px]">{p.body}</Body>
              </div>
            </div>
          ))}
        </div>

        {pick(data.highlight_title, lang) && (
          <div className="mt-16 md:mt-28" data-testid="special-highlight">
            <Kicker>{pick(data.highlight_title, lang)}</Kicker>
            <Body className="mt-3">{pick(data.highlight_body, lang)}</Body>
          </div>
        )}

        <div className="mt-14 md:mt-16">
          <div className="font-bold uppercase text-[22px] md:text-[27px] leading-tight" style={{ fontFamily: TIMES, color: INK }} translate="no">GLOBAL AFRIQUE INTERNATIONAL</div>
          <div className="italic text-[19px] md:text-[22px] mt-1" style={{ fontFamily: TIMES, color: INK }}>
            {lang === "fr" ? "L'Afrique au cœur du monde." : "Africa at the heart of the world."}
          </div>
        </div>
      </div>
    </Sheet>
  );
}
